import {
  AutoHedgingStatus,
  Box,
  ButtonVariants,
  CurrencyIcon,
  Divider,
  Flex,
  FormControlSizes,
  HStack,
  HedgeControlStatusEnum,
  HelpIcon,
  InlineFormattedNumber,
  ModeEnum,
  PositionThresholdMeter,
  SecurityRenderer,
  Text,
  ToggleGroup,
  toBigWithDefault,
  useCurrenciesContext,
  useCurrencyConversionRateValue,
  useObservableValue,
  useSecuritiesContext,
  useSubscription,
  type IHedgeRule,
  type MapTo,
  type Position,
  type Security,
} from '@talos/kyoko';
import Big, { type BigSource } from 'big.js';
import { useEffect, useMemo, useState } from 'react';
import { map } from 'rxjs';
import styled from 'styled-components';
import type { IHedgePositionStatusRow } from '../../providers/HedgePositionStatusProvider';
import {
  getCurrentPositionSubscriptionRequest,
  getMonitoredPositionAmount,
  getMonitoredPositionCurrency,
  getPositionCardCurrencyOptions,
} from './currentPositionLookup';
import type { EditIHedgeRuleForm, HedgeMonitoredAsset } from './types';

function isMonitoredSecurityAsset(asset: HedgeMonitoredAsset): asset is Security {
  return 'ProductType' in asset;
}

/**
 * Card to display the current position and status of a hedge rule. Optionally leverages the status to render a meter.
 */
export function PositionAutoHedgingCard({
  form,
  hedgeRule,
  hedgePositionStatus,
  mode,
  position: convertedPosition,
  source,
}: {
  form: EditIHedgeRuleForm;
  hedgePositionStatus: IHedgePositionStatusRow | undefined;
  hedgeRule: IHedgeRule | undefined;
  mode: ModeEnum;
  position: BigSource | undefined;
  source: 'hedgePositionStatus' | 'conversion' | 'none';
}) {
  const shortThreshold = form.ShortPositionLimit.value;
  const longThreshold = form.LongPositionLimit.value;
  const targetPosition = form.TargetPosition.value;

  const updated = useMemo(
    () =>
      ({
        LongPositionLimit: !toBigWithDefault(longThreshold, 0).eq(toBigWithDefault(hedgeRule?.LongPositionLimit, 1)),
        ShortPositionLimit: !toBigWithDefault(shortThreshold, 0).eq(toBigWithDefault(hedgeRule?.ShortPositionLimit, 1)),
        TargetPosition: !toBigWithDefault(targetPosition, 0).eq(toBigWithDefault(hedgeRule?.TargetPosition, 1)),
        AccountID: !(hedgeRule?.AccountID === form.AccountID.value?.SubaccountID),
        Asset: !(hedgeRule?.Asset === form.Asset.value?.Symbol),
        TargetAsset: !(hedgeRule?.TargetAsset === form.TargetAsset.value?.Symbol),
      }) satisfies Partial<MapTo<IHedgeRule, boolean>>,
    [
      form.AccountID.value?.SubaccountID,
      form.Asset.value?.Symbol,
      form.TargetAsset.value?.Symbol,
      hedgeRule?.AccountID,
      hedgeRule?.Asset,
      hedgeRule?.LongPositionLimit,
      hedgeRule?.ShortPositionLimit,
      hedgeRule?.TargetAsset,
      hedgeRule?.TargetPosition,
      longThreshold,
      shortThreshold,
      targetPosition,
    ]
  );

  const hedgeControlStatus =
    hedgePositionStatus?.HedgeControlStatus ??
    (mode === ModeEnum.Disabled ? HedgeControlStatusEnum.Disabled : HedgeControlStatusEnum.Waiting);

  const positionThresholdMeterProps = useMemo(() => {
    return {
      CurrentPosition: convertedPosition?.toString(),
      HedgeControlStatus: hedgeControlStatus,
      LongPositionLimit: longThreshold,
      ShortPositionLimit: shortThreshold,
      TargetAsset: form.TargetAsset.value?.Symbol,
      TargetPosition: targetPosition,
    };
  }, [
    convertedPosition,
    form.TargetAsset.value?.Symbol,
    hedgeControlStatus,
    longThreshold,
    shortThreshold,
    targetPosition,
  ]);

  const { securitiesBySymbol } = useSecuritiesContext();
  const { currenciesBySymbol } = useCurrenciesContext();
  const activeTargetAsset = form.TargetAsset.value?.Symbol ?? hedgeRule?.TargetAsset;
  const activeAsset = form.Asset.value?.Symbol ?? hedgeRule?.Asset;

  const availableCurrencies = useMemo(() => {
    return getPositionCardCurrencyOptions(activeTargetAsset, activeAsset, securitiesBySymbol, currenciesBySymbol).map(
      currency => ({
        value: currency,
        displayName: currency,
      })
    );
  }, [activeAsset, activeTargetAsset, currenciesBySymbol, securitiesBySymbol]);
  const [displayCurrency, handleOnDisplayCurrencyChange] = useState(availableCurrencies.at(0)?.value);
  useEffect(() => {
    handleOnDisplayCurrencyChange(availableCurrencies.at(0)?.value);
  }, [availableCurrencies]);

  const conversionRate = useCurrencyConversionRateValue(activeTargetAsset, displayCurrency);
  const shouldShowConvertedDisplay = !!conversionRate?.Rate && displayCurrency !== activeTargetAsset;
  const unableToConvert =
    displayCurrency !== undefined && displayCurrency !== activeTargetAsset && !conversionRate?.Rate;

  return (
    <CardWrapper fontSize="fontSizeSmall" data-testid="position-auto-hedging-card">
      <HStack justifyContent="space-between">
        <Text>Monitored Rollup</Text>
        <Text fontStyle={updated.AccountID ? 'italic' : undefined} color="colorTextImportant">
          {form.AccountID.value?.DisplayName ?? '-'}
        </Text>
      </HStack>
      <HStack justifyContent="space-between">
        <Text>Monitored Asset</Text>
        <HStack color="colorTextImportant">
          {!form.Asset.value ? (
            '-'
          ) : isMonitoredSecurityAsset(form.Asset.value) ? (
            <SecurityRenderer security={form.Asset.value} iconSize={12} showDescription={false} />
          ) : (
            <>
              <CurrencyIcon currency={form.Asset.value.Symbol} size={12} />
              <Box ml="spacing4">
                <Text fontStyle={updated.Asset ? 'italic' : undefined}>{form.Asset.value.Symbol}</Text>
              </Box>
            </>
          )}
        </HStack>
      </HStack>

      <>
        <HStack justifyContent="space-between">
          <Text>Status</Text>
          <AutoHedgingStatus status={hedgeControlStatus} text={hedgePositionStatus?.Text} />
        </HStack>
        <Divider />
        <HStack justifyContent="space-between" data-testid="current-position">
          <Flex gap="spacing4" alignItems="center">
            <Text>Current Position</Text>
            {availableCurrencies.length > 1 && displayCurrency && (
              <ToggleGroup
                data-testid="display-currency-toggle"
                defaultSelectedVariant={ButtonVariants.Priority}
                options={availableCurrencies}
                onChange={handleOnDisplayCurrencyChange}
                value={displayCurrency}
                size={FormControlSizes.Xxs}
              />
            )}
            {unableToConvert && (
              <HelpIcon data-testid="no-conversion-rate-help-icon" tooltip="No conversion rate available" />
            )}
          </Flex>
          <Box>
            {convertedPosition === undefined ? (
              '-'
            ) : (
              <InlineFormattedNumber
                round
                estimate={source === 'conversion' || shouldShowConvertedDisplay}
                number={Big(convertedPosition).times((shouldShowConvertedDisplay && conversionRate?.Rate) || 1)}
                currency={shouldShowConvertedDisplay ? displayCurrency : activeTargetAsset}
              />
            )}
          </Box>
        </HStack>

        <PositionThresholdMeter
          value={positionThresholdMeterProps}
          isUpdatedRecord={updated}
          displayCurrency={shouldShowConvertedDisplay ? displayCurrency : undefined}
          displayCurrencyConversion={shouldShowConvertedDisplay ? conversionRate : undefined}
        />
      </>
    </CardWrapper>
  );
}

export const useCurrentPosition = ({
  account,
  asset,
  hedgePositionStatus,
  targetAsset,
}: {
  asset: string | undefined;
  account: string | undefined;
  targetAsset: string | undefined;
  hedgePositionStatus: IHedgePositionStatusRow | undefined;
}): {
  position: BigSource | undefined;
  source: 'hedgePositionStatus' | 'conversion' | 'none';
} => {
  const { securitiesBySymbol } = useSecuritiesContext();
  const { currenciesBySymbol } = useCurrenciesContext();
  const shouldRequestPosition = !!(
    asset &&
    account &&
    // only request for a new position if we are sure the current hedgePositionStatus does not carry the correct information
    (targetAsset !== hedgePositionStatus?.TargetAsset || !hedgePositionStatus)
  );

  const request = useMemo(
    () =>
      shouldRequestPosition && asset && account
        ? getCurrentPositionSubscriptionRequest(asset, account, securitiesBySymbol, currenciesBySymbol)
        : null,
    [shouldRequestPosition, asset, account, securitiesBySymbol, currenciesBySymbol]
  );

  const { data: positionData } = useSubscription<Position>(request);

  const position = useObservableValue(() => positionData.pipe(map(json => json.data.at(0))), [positionData]);

  const conversionCurrency =
    shouldRequestPosition && asset
      ? getMonitoredPositionCurrency(asset, securitiesBySymbol, currenciesBySymbol)
      : undefined;
  const currencyConversionRate = useCurrencyConversionRateValue(
    conversionCurrency,
    shouldRequestPosition ? targetAsset : undefined
  );

  if (hedgePositionStatus?.CurrentPosition && !shouldRequestPosition) {
    // We have the position, and it is matching the target asset, meaning we show the hedge position untouched
    return { position: hedgePositionStatus.CurrentPosition, source: 'hedgePositionStatus' };
  }

  if (shouldRequestPosition && position?.Amount && asset) {
    const converted = getMonitoredPositionAmount(position.Amount, asset, securitiesBySymbol, currenciesBySymbol);
    if (!converted) {
      return { position: undefined, source: 'none' };
    }
    if (converted.currency === targetAsset) {
      return { position: converted.value, source: 'conversion' };
    }
    if (currencyConversionRate?.Rate) {
      return { position: Big(converted.value).times(currencyConversionRate.Rate), source: 'conversion' };
    }
  }
  // No hedgePositionStatus nor possible conversion, return undefined
  return { position: undefined, source: 'none' };
};

const CardWrapper = styled(Flex)`
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing8}px;
  background-color: ${({ theme }) => theme.backgroundCard};
  border-radius: ${({ theme }) => theme.borderRadiusCard}px;
  padding: ${({ theme }) => theme.spacing8}px;
`;
